import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  CreditCard,
  CheckCircle,
  Loader2,
  Lock,
  Shield,
  Edit,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Check,
  BarChart3,
  Users,
  FileUp,
  Sparkles,
  AlertCircle,
  Info,
  Crown,
  Plus,
  QrCode,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  validateProfileForSubscription,
  ProfileData,
} from "@/lib/profile-validation";
import { fetchPlanForUser, fetchPlansForUser } from "@/lib/subscription-plans";
import {
  CardData,
  validateCardNumber,
  formatCardNumber,
  getCardBrand,
} from "@/lib/pagarme";
import {
  formatCPF,
  formatPhone,
  formatCEP,
  formatPrice,
  formatDate,
} from "@/lib/formatters";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { getEdgeFunctionErrorMessage } from "@/lib/supabase-functions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planCode: string;
  planName: string;
  planPrice: number;
  onSuccess: () => void;
}

type WizardStep =
  | "profile-review"
  | "payment-method"
  | "card-form"
  | "processing"
  | "success"
  | "pix-success";

// Função para validar cartões de teste do Pagar.me
// isTestEnv: se false (produção), aceita cartões reais; se true (teste), apenas cartões de teste
function validateTestCard(
  cardNumber: string,
  cvv: string,
  isTestEnv: boolean = true,
): { valid: boolean; message?: string } {
  const cleaned = cardNumber.replace(/\s/g, "");

  // Cartões de teste válidos do Pagar.me
  const validTestCards = [
    "4000000000000010", // Aprovado
    "4000000000000028", // Recusado (para testar falhas)
    "4000000000000036", // Processing
  ];

  if (validTestCards.includes(cleaned)) {
    if (cvv !== "123") {
      return {
        valid: false,
        message: "⚠️ Para cartões de teste do Pagar.me, use CVV: 123",
      };
    }

    // Cartão de teste válido
    return { valid: true };
  }

  // Se não for um cartão de teste específico, validar pelo algoritmo de Luhn
  if (!validateCardNumber(cleaned)) {
    return {
      valid: false,
      message: "❌ Número de cartão inválido (falha no algoritmo de Luhn)",
    };
  }

  // Se for ambiente de produção, aceitar cartões reais válidos
  if (!isTestEnv) {
    return { valid: true };
  }

  // Aviso: cartão válido pelo Luhn mas pode não funcionar no ambiente de teste
  return {
    valid: false,
    message: "⚠️ Use um dos cartões de teste do Pagar.me listados acima",
  };
}

// Componente de resumo do plano
function PlanSummaryHeader({
  planName,
  planPrice,
  maxPatients,
  onChangePlan,
}: {
  planName: string;
  planPrice: number;
  maxPatients: number | null;
  onChangePlan?: () => void;
}) {
  return (
    <div className="bg-gradient-to-br from-primary/10 via-purple-500/5 to-primary/10 rounded-xl p-6 mb-6 border border-primary/20">
      <div className="flex items-start justify-between gap-4">
        {/* Lado esquerdo: Informações do plano */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Crown className="h-6 w-6 text-primary" />
            </div>
            <div>
              <Badge variant="secondary" className="mb-1">
                Plano Selecionado
              </Badge>
              <h3 className="text-2xl font-bold text-foreground">{planName}</h3>
            </div>
          </div>

          {/* Lista de benefícios */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-primary" />
              <span>
                {maxPatients === null
                  ? "Pacientes ilimitados"
                  : `Até ${maxPatients} pacientes`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileUp className="h-4 w-4 text-primary" />
              <span>Upload sem limites</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>Análises com IA</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <BarChart3 className="h-4 w-4 text-primary" />
              <span>Relatórios avançados</span>
            </div>
          </div>
        </div>

        {/* Lado direito: Preço */}
        <div className="text-right">
          <p className="text-sm text-muted-foreground mb-1">A partir de</p>
          <div className="text-4xl font-bold text-primary">
            {formatPrice(planPrice)}
          </div>
          <p className="text-sm text-muted-foreground mt-1">por mês</p>
          <Badge variant="outline" className="mt-2">
            <Calendar className="h-3 w-3 mr-1" />
            Cobrança mensal
          </Badge>
          {onChangePlan && (
            <Button
              variant="outline"
              size="sm"
              onClick={onChangePlan}
              className="mt-2 w-full"
            >
              <Edit className="h-3 w-3 mr-1" />
              Trocar plano
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente de seleção de plano
function PlanSelectorDialog({
  open,
  onOpenChange,
  plans,
  currentPlanCode,
  onSelectPlan,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: any[];
  currentPlanCode: string;
  onSelectPlan: (plan: any) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Escolha seu plano</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {plans.map((plan: any) => (
            <Card
              key={plan.code}
              className={cn(
                "cursor-pointer transition-all hover:shadow-lg",
                plan.code === currentPlanCode && "ring-2 ring-primary",
              )}
              onClick={() => {
                onSelectPlan(plan);
                onOpenChange(false);
              }}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{plan.name}</CardTitle>
                  {plan.code === currentPlanCode && <Badge>Selecionado</Badge>}
                </div>
                <div className="text-3xl font-bold text-primary">
                  {formatPrice(plan.price_cents)}
                  <span className="text-sm font-normal text-muted-foreground">
                    /mês
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4 text-primary" />
                    {plan.max_patients === null
                      ? "Pacientes ilimitados"
                      : `Até ${plan.max_patients} pacientes`}
                  </div>
                  {plan.features?.map((feature: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Componente de indicador de progresso
function StepIndicator({ currentStep }: { currentStep: WizardStep }) {
  const steps = ["profile-review", "payment-method", "card-form", "success"];
  const isSuccess = currentStep === "success" || currentStep === "pix-success";
  const stepForIndex = currentStep === "processing" ? "card-form" : currentStep;
  const currentStepIndex = isSuccess
    ? steps.length
    : Math.max(0, steps.indexOf(stepForIndex));

  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = !isSuccess && index === currentStepIndex;
        const isCompleted = index < currentStepIndex || isSuccess;

        return (
          <div key={step} className="flex items-center gap-2">
            <motion.div
              initial={false}
              animate={{
                scale: isActive
                  ? 1.1
                  : isSuccess && index === steps.length - 1
                  ? 1.1
                  : 1,
              }}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all",
                isActive &&
                  "bg-primary text-primary-foreground ring-4 ring-primary/20",
                isCompleted && "bg-green-600 text-white",
                !isActive && !isCompleted && "bg-muted text-muted-foreground",
                isSuccess &&
                  index === steps.length - 1 &&
                  "ring-4 ring-green-500/30",
              )}
            >
              {isCompleted ? <Check className="h-4 w-4" /> : stepNumber}
            </motion.div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "w-12 h-1 rounded-full transition-colors",
                  isCompleted ? "bg-green-600" : "bg-muted",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Assinatura mais recente não cancelada (mesma regra de `pagarme-upgrade-subscription`). */
async function fetchLatestNonCanceledSubscriptionForUser(userId: string) {
  const { data } = await supabase
    .from("subscriptions")
    .select("id, plan_code")
    .eq("user_id", userId)
    .neq("status", "canceled")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ?? null;
}

function shouldUsePagarmeUpgradeSubscription(
  existing: { plan_code: string } | null,
  targetPlanCode: string,
): boolean {
  if (!existing) return false;
  return existing.plan_code !== targetPlanCode;
}

export function SubscriptionModal({
  open,
  onOpenChange,
  planCode,
  planName,
  planPrice,
  onSuccess,
}: SubscriptionModalProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>("profile-review");
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const [progressMessage, setProgressMessage] = useState<string>("");
  const [subscriptionResult, setSubscriptionResult] = useState<{
    subscriptionId: string;
    nextBillingDate: string;
  } | null>(null);
  const [pixResult, setPixResult] = useState<{
    pix_qr_code: string;
    pix_qr_code_url: string | null;
    amount_cents: number;
    plan_name: string;
  } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "pix" | null>(
    null,
  );
  const [cardData, setCardData] = useState<CardData>({
    number: "",
    holder_name: "",
    exp_month: "",
    exp_year: "",
    cvv: "",
  });
  const [existingCards, setExistingCards] = useState<
    Array<{
      id: string;
      last_four_digits: string;
      brand: string;
      holder_name: string;
      is_default?: boolean;
    }>
  >([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editableProfile, setEditableProfile] = useState<ProfileData>({
    full_name: "",
    cpf: "",
    phone: "",
    address_zipcode: "",
    address_street: "",
    address_number: "",
    address_complement: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
  });

  // Estado para dados completos do plano
  const [fullPlanData, setFullPlanData] = useState<{
    code: string;
    name: string;
    price_cents: number;
    max_patients: number | null;
    features: any;
  } | null>(null);

  // Estado para seletor de planos
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);

  // Verificar se é ambiente de teste baseado na URL
  // app.elinia.ai/app -> PRODUÇÃO (não é teste)
  const isTestEnv =
    typeof window !== "undefined" &&
    !window.location.href.includes("app.elinia.ai/app") &&
    !window.location.href.includes("app.elinia.ai");

  // Ref para focar no input do número do cartão
  const cardNumberInputRef = useRef<HTMLInputElement>(null);

  // Buscar dados completos do plano selecionado (com preço da tabela do usuário)
  useEffect(() => {
    const fetchPlanData = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !planCode) return;

      const plan = await fetchPlanForUser(user.id, planCode);
      if (plan) {
        setFullPlanData({
          code: plan.code,
          name: plan.name,
          price_cents: plan.price_cents,
          max_patients: plan.max_patients,
          features: plan.features,
        });
      }
    };

    if (planCode) {
      fetchPlanData();
    }
  }, [planCode]);

  // Buscar todos os planos disponíveis (com preços da tabela do usuário)
  useEffect(() => {
    const fetchAllPlans = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const plans = await fetchPlansForUser(user.id, {
        excludeFree: true,
        orderBy: "display_order",
      });
      setAvailablePlans(plans);
    };

    fetchAllPlans();
  }, []);

  // Função para trocar de plano
  const handleChangePlan = (newPlan: any) => {
    setFullPlanData({
      code: newPlan.code,
      name: newPlan.name,
      price_cents: newPlan.price_cents,
      max_patients: newPlan.max_patients,
      features: newPlan.features,
    });

    toast.success(`Plano alterado para ${newPlan.name}`);
  };

  useEffect(() => {
    if (open) {
      loadProfile();
      loadExistingCards();
    } else {
      // Reset quando fechar
      setCurrentStep("profile-review");
      setCardData({
        number: "",
        holder_name: "",
        exp_month: "",
        exp_year: "",
        cvv: "",
      });
      setExistingCards([]);
      setSelectedCardId(null);
      setUseNewCard(true);
      setSubscriptionResult(null);
      setPixResult(null);
      setPaymentMethod(null);
    }
  }, [open]);

  // Auto-preencher nome no cartão quando entrar no passo 2
  useEffect(() => {
    if (
      currentStep === "card-form" &&
      profileData?.full_name &&
      !cardData.holder_name
    ) {
      console.log(
        "[SubscriptionModal] Auto-preenchendo nome no cartão:",
        profileData.full_name,
      );
      setCardData((prev) => ({
        ...prev,
        holder_name: profileData.full_name.toUpperCase(),
      }));
    }
  }, [currentStep, profileData]);

  // Focar automaticamente no campo número do cartão (quando for novo cartão)
  useEffect(() => {
    if (
      currentStep === "card-form" &&
      (useNewCard || existingCards.length === 0)
    ) {
      setTimeout(() => {
        cardNumberInputRef.current?.focus();
      }, 100);
    }
  }, [currentStep, useNewCard, existingCards.length]);

  async function loadExistingCards() {
    try {
      const { data, error } = await supabase.functions.invoke(
        "pagarme-list-cards",
      );
      if (error) throw error;
      if (data?.cards && data.cards.length > 0) {
        setExistingCards(data.cards);
        setUseNewCard(false);
        setSelectedCardId(
          data.cards.find((c: any) => c.is_default)?.id || data.cards[0].id,
        );
      }
    } catch (err) {
      console.error("[SubscriptionModal] Erro ao carregar cartões:", err);
    }
  }

  async function loadProfile() {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Sessão não encontrada");
        onOpenChange(false);
        return;
      }

      setUserEmail(user.email || "");

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      const validation = validateProfileForSubscription(profile || {});

      // Sempre permite abrir o modal
      setIsProfileComplete(validation.valid);
      setEditMode(!validation.valid); // Se incompleto, ativar edit mode

      if (!validation.valid) {
        // Apenas avisar quais dados faltam (sem fechar o modal)
        toast.info(
          `Complete os seguintes dados: ${validation.missing.join(", ")}`,
          { duration: 5000 },
        );
      }

      // Preencher editableProfile com dados existentes (mesmo que parciais)
      setEditableProfile({
        full_name: profile?.full_name || "",
        cpf: profile?.cpf || "",
        phone: profile?.phone || "",
        address_zipcode: profile?.address_zipcode || "",
        address_street: profile?.address_street || "",
        address_number: profile?.address_number || "",
        address_complement: profile?.address_complement || "",
        address_neighborhood: profile?.address_neighborhood || "",
        address_city: profile?.address_city || "",
        address_state: profile?.address_state || "",
      });

      setProfileData(profile);
    } catch (error: any) {
      console.error("Error loading profile:", error);
      toast.error("Erro ao carregar perfil");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveProfile() {
    setLoading(true);
    setProgressMessage("💾 Salvando dados do perfil...");

    try {
      // Validar dados editados
      const validation = validateProfileForSubscription(editableProfile);

      if (!validation.valid) {
        toast.error(
          `Campos obrigatórios faltando: ${validation.missing.join(", ")}`,
        );
        setLoading(false);
        setProgressMessage("");
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão não encontrada");

      // Atualizar perfil no banco
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: editableProfile.full_name,
          cpf: editableProfile.cpf?.replace(/\D/g, ""),
          phone: editableProfile.phone?.replace(/\D/g, ""),
          address_zipcode: editableProfile.address_zipcode?.replace(/\D/g, ""),
          address_street: editableProfile.address_street,
          address_number: editableProfile.address_number,
          address_complement: editableProfile.address_complement,
          address_neighborhood: editableProfile.address_neighborhood,
          address_city: editableProfile.address_city,
          address_state: editableProfile.address_state,
        })
        .eq("id", user.id);

      if (error) throw error;

      // Buscar perfil atualizado do banco para garantir sincronização
      const { data: updatedProfile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      // Atualizar state com dados do banco
      setProfileData(updatedProfile);
      setIsProfileComplete(true);
      setEditMode(false);

      toast.success("Perfil atualizado com sucesso!");

      // Avançar para criação do cliente no Pagar.me
      await handleContinueToCard();
    } catch (error: any) {
      console.error("Error saving profile:", error);
      toast.error("Erro ao salvar perfil");
      setLoading(false);
      setProgressMessage("");
    }
  }

  async function handleCepBlur() {
    const cep = editableProfile.address_zipcode?.replace(/\D/g, "");
    if (!cep || cep.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }

      setEditableProfile((prev) => ({
        ...prev,
        address_street: data.logradouro || prev.address_street,
        address_neighborhood: data.bairro || prev.address_neighborhood,
        address_city: data.localidade || prev.address_city,
        address_state: data.uf || prev.address_state,
      }));

      toast.success("Endereço preenchido automaticamente!");
    } catch (error) {
      console.error("Error fetching CEP:", error);
    }
  }

  async function handleContinueToCard() {
    setLoading(true);
    setProgressMessage("👤 Verificando cliente...");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Sessão não encontrada");
      }

      // Usar editableProfile se estiver em modo de edição, senão usar profileData
      const currentProfile = editMode ? editableProfile : profileData;

      if (!currentProfile) {
        throw new Error("Dados do perfil não encontrados");
      }

      // Validar dados antes de criar cliente
      const validation = validateProfileForSubscription(currentProfile);
      if (!validation.valid) {
        toast.error(
          `Campos obrigatórios faltando: ${validation.missing.join(", ")}`,
        );
        setLoading(false);
        setProgressMessage("");
        return;
      }

      let customerId = profileData?.pagarme_customer_id;

      // Se não tem customer_id, criar novo
      if (!customerId) {
        console.log("[SubscriptionModal] Criando novo cliente no Pagar.me...");

        const {
          data: customerData,
          error: customerError,
        } = await supabase.functions.invoke("pagarme-create-customer", {
          body: {
            name: currentProfile.full_name,
            email: session.user.email,
            document: currentProfile.cpf?.replace(/\D/g, ""),
            phone: currentProfile.phone?.replace(/\D/g, ""),
            address: {
              street: currentProfile.address_street,
              number: currentProfile.address_number,
              complement: currentProfile.address_complement || "",
              neighborhood: currentProfile.address_neighborhood,
              city: currentProfile.address_city,
              state: currentProfile.address_state,
              zipcode: currentProfile.address_zipcode,
              country: "BR",
            },
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (customerError) {
          console.error(
            "[SubscriptionModal] Erro ao criar cliente:",
            customerError,
          );
          throw new Error(
            await getEdgeFunctionErrorMessage(
              customerError,
              null,
              "Erro ao criar cliente",
            ),
          );
        }

        customerId = customerData.customer_id;
        console.log("[SubscriptionModal] ✅ Cliente criado:", customerId);

        // Atualizar profileData com o novo customer_id
        setProfileData({ ...profileData, pagarme_customer_id: customerId });

        toast.success("Cliente cadastrado com sucesso!");
      } else {
        console.log("[SubscriptionModal] ✅ Cliente já existe:", customerId);
      }

      // Avançar para escolha de forma de pagamento
      setCurrentStep("payment-method");
    } catch (error: any) {
      console.error(
        "[SubscriptionModal] Erro ao verificar/criar cliente:",
        error,
      );
      toast.error(error.message || "Erro ao verificar cliente");
    } finally {
      setLoading(false);
      setProgressMessage("");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Se usar cartão existente, validar seleção
    if (!useNewCard) {
      if (!selectedCardId) {
        toast.error("Selecione um cartão");
        return;
      }
    } else {
      // Validações para novo cartão
      if (
        !cardData.number ||
        !cardData.holder_name ||
        !cardData.exp_month ||
        !cardData.exp_year ||
        !cardData.cvv
      ) {
        toast.error("Preencha todos os campos do cartão");
        return;
      }

      const cardValidation = validateTestCard(
        cardData.number,
        cardData.cvv,
        isTestEnv,
      );
      if (!cardValidation.valid) {
        toast.error(cardValidation.message || "Cartão inválido", {
          duration: 8000,
        });
        return;
      }

      const expMonth = parseInt(cardData.exp_month);
      const expYear = parseInt(cardData.exp_year);

      if (expMonth < 1 || expMonth > 12) {
        toast.error("Mês de validade inválido");
        return;
      }

      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;
      if (
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth)
      ) {
        toast.error("Cartão vencido");
        return;
      }
    }

    setLoading(true);
    setCurrentStep("processing");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session || !profileData) {
        throw new Error("Sessão não encontrada");
      }

      const customerId = profileData.pagarme_customer_id;

      if (!customerId) {
        throw new Error(
          "Cliente não encontrado. Por favor, volte ao passo anterior.",
        );
      }

      let cardUuid: string;

      if (useNewCard) {
        // 1. Cadastrar novo cartão
        setProgressMessage("💳 Cadastrando cartão...");
        const {
          data: cardResponse,
          error: cardError,
        } = await supabase.functions.invoke("pagarme-create-card-direct", {
          body: {
            card_number: cardData.number.replace(/\s/g, ""),
            card_holder_name: cardData.holder_name,
            card_exp_month: cardData.exp_month,
            card_exp_year: cardData.exp_year,
            card_cvv: cardData.cvv,
            set_as_default: true,
            pagarme_customer_id: customerId,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (cardError) {
          throw new Error(
            await getEdgeFunctionErrorMessage(
              cardError,
              null,
              "Erro ao cadastrar cartão",
            ),
          );
        }
        if (!cardResponse?.card?.id)
          throw new Error("Resposta inválida ao cadastrar cartão");
        cardUuid = cardResponse.card.id;
      } else {
        // Usar cartão já cadastrado
        cardUuid = selectedCardId!;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão não encontrada");

      const targetPlanCode = fullPlanData?.code || planCode;
      const existingSub = await fetchLatestNonCanceledSubscriptionForUser(
        user.id,
      );
      const useUpgrade = shouldUsePagarmeUpgradeSubscription(
        existingSub,
        targetPlanCode,
      );

      if (useUpgrade) {
        setProgressMessage("✨ Processando alteração de plano...");
        const { data: upgradeData, error: upgradeError } =
          await supabase.functions.invoke("pagarme-upgrade-subscription", {
            body: {
              new_plan_code: targetPlanCode,
              card_id: cardUuid,
            },
            headers: { Authorization: `Bearer ${session.access_token}` },
          });

        if (upgradeError) {
          throw new Error(
            await getEdgeFunctionErrorMessage(
              upgradeError,
              null,
              "Erro ao alterar plano",
            ),
          );
        }

        if (!upgradeData?.success) {
          throw new Error(upgradeData?.error || "Erro ao alterar plano");
        }

        const { data: subRow } = await supabase
          .from("subscriptions")
          .select("id, next_billing_date")
          .eq("id", existingSub!.id)
          .single();

        const nextBillingDate = subRow?.next_billing_date
          ? new Date(`${subRow.next_billing_date}T12:00:00Z`).toISOString()
          : (() => {
              const d = new Date();
              d.setMonth(d.getMonth() + 1);
              return d.toISOString();
            })();

        setSubscriptionResult({
          subscriptionId: subRow?.id ?? existingSub!.id,
          nextBillingDate,
        });

        setCurrentStep("success");
        toast.success(
          "Pagamento aprovado! Seu plano será atualizado em instantes.",
        );
      } else {
        setProgressMessage("✨ Ativando assinatura...");
        const {
          data: subscriptionData,
          error: subscriptionError,
        } = await supabase.functions.invoke("pagarme-create-subscription", {
          body: {
            plan_code: targetPlanCode,
            card_id: cardUuid,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (subscriptionError) {
          throw new Error(
            await getEdgeFunctionErrorMessage(
              subscriptionError,
              null,
              "Erro ao criar assinatura",
            ),
          );
        }

        if (!subscriptionData?.success) {
          throw new Error(
            subscriptionData?.error || "Erro ao criar assinatura",
          );
        }

        const nextBilling = new Date();
        nextBilling.setMonth(nextBilling.getMonth() + 1);

        setSubscriptionResult({
          subscriptionId: subscriptionData.subscription.id,
          nextBillingDate: nextBilling.toISOString(),
        });

        setCurrentStep("success");
      }
    } catch (error: any) {
      console.error("Error creating subscription:", error);
      toast.error(error.message || "Erro ao criar assinatura");
      setCurrentStep("card-form");
    } finally {
      setLoading(false);
      setProgressMessage("");
    }
  }

  async function handleSubmitPix() {
    setLoading(true);
    setCurrentStep("processing");
    setProgressMessage("Gerando PIX...");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session || !profileData?.pagarme_customer_id) {
        throw new Error(
          "Sessão ou cliente não encontrado. Volte ao passo anterior.",
        );
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão não encontrada");

      const targetPlanCode = fullPlanData?.code || planCode;
      const existingSub = await fetchLatestNonCanceledSubscriptionForUser(
        user.id,
      );
      const useUpgrade = shouldUsePagarmeUpgradeSubscription(
        existingSub,
        targetPlanCode,
      );

      type PixFnData = {
        success?: boolean;
        error?: string;
        pix_qr_code?: string;
        pix_qr_code_url?: string | null;
        amount_cents?: number;
        plan_name?: string;
      };

      let pixPayload: PixFnData;
      let invokeError: unknown;

      if (useUpgrade) {
        const res = await supabase.functions.invoke(
          "pagarme-upgrade-subscription",
          {
            body: {
              new_plan_code: targetPlanCode,
              payment_method: "pix",
            },
            headers: { Authorization: `Bearer ${session.access_token}` },
          },
        );
        pixPayload = (res.data as PixFnData) ?? {};
        invokeError = res.error;
      } else {
        const res = await supabase.functions.invoke(
          "pagarme-create-subscription-pix",
          {
            body: { plan_code: targetPlanCode },
            headers: { Authorization: `Bearer ${session.access_token}` },
          },
        );
        pixPayload = (res.data as PixFnData) ?? {};
        invokeError = res.error;
      }

      if (invokeError) {
        throw new Error(
          await getEdgeFunctionErrorMessage(
            invokeError,
            null,
            "Erro ao gerar PIX",
          ),
        );
      }
      if (!pixPayload?.success) {
        throw new Error(pixPayload?.error || "Erro ao gerar PIX");
      }

      setPixResult({
        pix_qr_code: pixPayload.pix_qr_code!,
        pix_qr_code_url: pixPayload.pix_qr_code_url ?? null,
        amount_cents: pixPayload.amount_cents!,
        plan_name: pixPayload.plan_name!,
      });
      setCurrentStep("pix-success");
      toast.success(
        useUpgrade
          ? "PIX gerado! Pague para confirmar a alteração do plano."
          : "PIX gerado! Pague para ativar sua assinatura.",
      );
    } catch (err: any) {
      console.error("Error creating PIX subscription:", err);
      toast.error(err.message || "Erro ao gerar PIX");
      setCurrentStep("payment-method");
    } finally {
      setLoading(false);
      setProgressMessage("");
    }
  }

  // Handler customizado para fechar o modal
  const handleOpenChange = (newOpen: boolean) => {
    if (
      !newOpen &&
      (currentStep === "success" || currentStep === "pix-success")
    ) {
      onSuccess();
      navigate("/app");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {currentStep === "profile-review" &&
              "📋 Vamos começar com seus dados"}
            {currentStep === "payment-method" && "💳 Forma de pagamento"}
            {currentStep === "card-form" && "💳 Dados do cartão"}
            {currentStep === "processing" && "✨ Ativando sua assinatura"}
            {currentStep === "success" && "🎉 Pagamento Aprovado!"}
            {currentStep === "pix-success" && "📱 Pague via PIX"}
          </DialogTitle>
        </DialogHeader>

        {/* Welcome message and plan summary - shown for steps before success */}
        {currentStep !== "success" && currentStep !== "pix-success" && (
          <>
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-3">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Falta pouco para começar!
              </h2>
              <p className="text-muted-foreground">
                Complete seu cadastro em 3 passos simples e comece a usar agora
                mesmo
              </p>
            </div>

            <PlanSummaryHeader
              planName={fullPlanData?.name || planName}
              planPrice={fullPlanData?.price_cents || planPrice}
              maxPatients={fullPlanData?.max_patients ?? null}
              onChangePlan={
                currentStep === "card-form" ||
                currentStep === "profile-review" ||
                currentStep === "payment-method"
                  ? () => setShowPlanSelector(true)
                  : undefined
              }
            />

            <StepIndicator currentStep={currentStep} />
          </>
        )}

        {/* Step indicator for success steps */}
        {(currentStep === "success" || currentStep === "pix-success") && (
          <StepIndicator currentStep={currentStep} />
        )}

        <AnimatePresence mode="wait">
          {/* PASSO 1: REVISÃO DE DADOS DO CLIENTE */}
          {currentStep === "profile-review" && (
            <motion.div
              key="profile-review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {/* Scroll-down pin indicator */}
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById("profile-data-section");
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="group mx-auto flex flex-col items-center gap-1.5 py-2 cursor-pointer select-none focus:outline-none"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80 group-hover:text-foreground transition-colors">
                  Confira seus dados antes de continuar
                </span>
                <motion.div
                  animate={{ y: [0, 6, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="flex items-center justify-center size-7 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors"
                >
                  <ChevronDown className="text-primary" />
                </motion.div>
              </button>

              {/* 🟢 MODO VISUALIZAÇÃO: Perfil completo */}
              {isProfileComplete && !editMode && profileData && (
                <>
                  <div
                    id="profile-data-section"
                    className="space-y-4 bg-muted/30 rounded-lg p-6 border border-muted"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Nome Completo
                        </Label>
                        <p className="font-medium">{profileData.full_name}</p>
                      </div>
                      <Badge variant="success" className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Verificado
                      </Badge>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          CPF
                        </Label>
                        <p className="text-sm">{formatCPF(profileData.cpf)}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Telefone
                        </Label>
                        <p className="text-sm">
                          {formatPhone(profileData.phone)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Email
                      </Label>
                      <p className="text-sm break-all">{userEmail}</p>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Endereço
                      </Label>
                      <p className="text-sm">
                        {profileData.address_street},{" "}
                        {profileData.address_number}
                        {profileData.address_complement &&
                          `, ${profileData.address_complement}`}
                        <br />
                        {profileData.address_neighborhood} -{" "}
                        {profileData.address_city}/{profileData.address_state}
                        <br />
                        CEP: {formatCEP(profileData.address_zipcode)}
                      </p>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setEditMode(true)}
                    >
                      <Edit className="h-3 w-3 mr-2" />
                      Editar dados
                    </Button>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/20 rounded-lg p-3">
                    <Lock className="h-4 w-4" />
                    <span>Seus dados estão protegidos e criptografados</span>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleContinueToCard}
                      className="flex-1 group"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verificando cliente...
                        </>
                      ) : (
                        <>
                          Continuar
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}

              {/* 🟡 MODO EDIÇÃO: Perfil incompleto ou usuário clicou em "Editar" */}
              {(!isProfileComplete || editMode) && (
                <form
                  id="profile-data-section"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveProfile();
                  }}
                  className="space-y-4"
                >
                  {!isProfileComplete && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-yellow-900">
                          Complete seu perfil
                        </p>
                        <p className="text-yellow-700 mt-1">
                          Preencha os dados abaixo para continuar com a
                          assinatura
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Dados pessoais */}
                  <div className="space-y-3 bg-muted/20 rounded-lg p-4 border">
                    <h4 className="font-medium text-sm">Dados Pessoais</h4>

                    <div className="space-y-2">
                      <Label htmlFor="full_name">Nome completo *</Label>
                      <Input
                        id="full_name"
                        value={editableProfile.full_name || ""}
                        onChange={(e) =>
                          setEditableProfile((prev) => ({
                            ...prev,
                            full_name: e.target.value,
                          }))
                        }
                        placeholder="Seu nome completo"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="cpf">CPF *</Label>
                        <Input
                          id="cpf"
                          value={formatCPF(editableProfile.cpf)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            setEditableProfile((prev) => ({
                              ...prev,
                              cpf: raw,
                            }));
                          }}
                          placeholder="000.000.000-00"
                          maxLength={14}
                          required
                          disabled={loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone *</Label>
                        <Input
                          id="phone"
                          value={formatPhone(editableProfile.phone)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            setEditableProfile((prev) => ({
                              ...prev,
                              phone: raw,
                            }));
                          }}
                          placeholder="(00) 00000-0000"
                          maxLength={15}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Endereço */}
                  <div className="space-y-3 bg-muted/20 rounded-lg p-4 border">
                    <h4 className="font-medium text-sm">Endereço</h4>

                    <div className="space-y-2">
                      <Label htmlFor="cep">CEP *</Label>
                      <Input
                        id="cep"
                        value={formatCEP(editableProfile.address_zipcode)}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, "");
                          setEditableProfile((prev) => ({
                            ...prev,
                            address_zipcode: raw,
                          }));
                        }}
                        onBlur={handleCepBlur}
                        placeholder="00000-000"
                        maxLength={9}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="street">Rua *</Label>
                      <Input
                        id="street"
                        value={editableProfile.address_street || ""}
                        onChange={(e) =>
                          setEditableProfile((prev) => ({
                            ...prev,
                            address_street: e.target.value,
                          }))
                        }
                        placeholder="Nome da rua"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="number">Número *</Label>
                        <Input
                          id="number"
                          value={editableProfile.address_number || ""}
                          onChange={(e) =>
                            setEditableProfile((prev) => ({
                              ...prev,
                              address_number: e.target.value,
                            }))
                          }
                          placeholder="123"
                          required
                          disabled={loading}
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="complement">Complemento</Label>
                        <Input
                          id="complement"
                          value={editableProfile.address_complement || ""}
                          onChange={(e) =>
                            setEditableProfile((prev) => ({
                              ...prev,
                              address_complement: e.target.value,
                            }))
                          }
                          placeholder="Apto, Bloco, etc (opcional)"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro *</Label>
                      <Input
                        id="neighborhood"
                        value={editableProfile.address_neighborhood || ""}
                        onChange={(e) =>
                          setEditableProfile((prev) => ({
                            ...prev,
                            address_neighborhood: e.target.value,
                          }))
                        }
                        placeholder="Bairro"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="city">Cidade *</Label>
                        <Input
                          id="city"
                          value={editableProfile.address_city || ""}
                          onChange={(e) =>
                            setEditableProfile((prev) => ({
                              ...prev,
                              address_city: e.target.value,
                            }))
                          }
                          placeholder="Cidade"
                          required
                          disabled={loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">Estado *</Label>
                        <Input
                          id="state"
                          value={editableProfile.address_state || ""}
                          onChange={(e) =>
                            setEditableProfile((prev) => ({
                              ...prev,
                              address_state: e.target.value.toUpperCase(),
                            }))
                          }
                          placeholder="UF"
                          maxLength={2}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email (não editável) */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Email (não editável)
                    </Label>
                    <p className="text-sm text-muted-foreground px-3 py-2 bg-muted/50 rounded-md">
                      {userEmail}
                    </p>
                  </div>

                  {/* Botões de ação */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (editMode && isProfileComplete) {
                          // Cancelar edição e voltar ao view mode
                          setEditMode(false);
                        } else {
                          // Fechar modal
                          onOpenChange(false);
                        }
                      }}
                      className="flex-1"
                      disabled={loading}
                    >
                      {editMode && isProfileComplete ? "Cancelar" : "Fechar"}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 group"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {progressMessage || "Salvando..."}
                        </>
                      ) : (
                        <>
                          Salvar e Continuar
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Badge de segurança */}
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/20 rounded-lg p-3">
                    <Lock className="h-4 w-4" />
                    <span>Seus dados estão protegidos e criptografados</span>
                  </div>
                </form>
              )}
            </motion.div>
          )}

          {/* PASSO 2: ESCOLHA DE FORMA DE PAGAMENTO */}
          {currentStep === "payment-method" && (
            <motion.div
              key="payment-method"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <p className="text-muted-foreground text-center mb-4">
                Escolha como deseja pagar sua assinatura
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card
                  className={cn(
                    "cursor-pointer transition-all hover:shadow-md border-2",
                    paymentMethod === "card"
                      ? "ring-2 ring-primary border-primary bg-primary/5"
                      : "border-transparent hover:border-muted-foreground/20",
                  )}
                  onClick={() => {
                    if (loading) return;
                    setPaymentMethod("card");
                    setTimeout(() => setCurrentStep("card-form"), 350);
                  }}
                >
                  <CardContent className="p-6 flex flex-col items-center gap-3">
                    <div
                      className={cn(
                        "flex items-center justify-center size-14 rounded-full transition-colors",
                        paymentMethod === "card" ? "bg-primary/15" : "bg-muted",
                      )}
                    >
                      <CreditCard
                        className={cn(
                          "h-7 w-7 transition-colors",
                          paymentMethod === "card"
                            ? "text-primary"
                            : "text-muted-foreground",
                        )}
                      />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold">Cartão de crédito</p>
                      <p className="text-xs text-muted-foreground">
                        Cobrança automática mensal
                      </p>
                    </div>
                    <div
                      className={cn(
                        "size-5 rounded-full border-2 flex items-center justify-center transition-all",
                        paymentMethod === "card"
                          ? "border-primary bg-primary"
                          : "border-muted-foreground/30",
                      )}
                    >
                      {paymentMethod === "card" && (
                        <Check className="text-primary-foreground h-3 w-3" />
                      )}
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className={cn(
                    "cursor-pointer transition-all hover:shadow-md border-2",
                    paymentMethod === "pix"
                      ? "ring-2 ring-primary border-primary bg-primary/5"
                      : "border-transparent hover:border-muted-foreground/20",
                  )}
                  onClick={() => {
                    if (loading) return;
                    setPaymentMethod("pix");
                    setTimeout(() => handleSubmitPix(), 350);
                  }}
                >
                  <CardContent className="p-6 flex flex-col items-center gap-3">
                    <div
                      className={cn(
                        "flex items-center justify-center size-14 rounded-full transition-colors",
                        paymentMethod === "pix" ? "bg-primary/15" : "bg-muted",
                      )}
                    >
                      <QrCode
                        className={cn(
                          "h-7 w-7 transition-colors",
                          paymentMethod === "pix"
                            ? "text-primary"
                            : "text-muted-foreground",
                        )}
                      />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold">PIX</p>
                      <p className="text-xs text-muted-foreground">
                        Pague via QR code ou copia e cola
                      </p>
                    </div>
                    <div
                      className={cn(
                        "size-5 rounded-full border-2 flex items-center justify-center transition-all",
                        paymentMethod === "pix"
                          ? "border-primary bg-primary"
                          : "border-muted-foreground/30",
                      )}
                    >
                      {paymentMethod === "pix" && (
                        <Check className="text-primary-foreground h-3 w-3" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep("profile-review")}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar
                </Button>
              </div>
            </motion.div>
          )}

          {/* PASSO 3: FORMULÁRIO DE CARTÃO */}
          {currentStep === "card-form" && (
            <motion.div
              key="card-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {/* Resumo do plano */}
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-4 border border-primary/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Você está assinando
                    </p>
                    <h3 className="text-lg font-bold">
                      {fullPlanData?.name || planName}
                    </h3>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">
                      {formatPrice(fullPlanData?.price_cents ?? planPrice)}
                    </p>
                    <Badge variant="secondary" className="mt-1">
                      <Calendar className="h-3 w-3 mr-1" />
                      Cobrança mensal
                    </Badge>
                  </div>
                </div>
              </div>

              {/* ✅ Alert com cartões de teste - apenas em ambiente de teste */}
              {isTestEnv && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 mb-1">
                        🧪 Ambiente de Teste
                      </p>
                      <p className="text-blue-700 mb-2">
                        Use um dos cartões de teste abaixo para simular
                        transações:
                      </p>
                      <div className="space-y-1.5 text-blue-700">
                        <div className="flex items-center gap-2 font-mono text-xs bg-white/50 rounded px-2 py-1.5">
                          <span className="font-semibold">✓ Aprovado:</span>
                          <span>4000 0000 0000 0010</span>
                        </div>
                        <div className="flex items-center gap-2 font-mono text-xs bg-white/50 rounded px-2 py-1.5">
                          <span className="font-semibold">✗ Recusado:</span>
                          <span>4000 0000 0000 0028</span>
                        </div>
                        <div className="mt-2 text-xs">
                          <strong>CVV:</strong> 123 • <strong>Validade:</strong>{" "}
                          12/30 • <strong>Qualquer nome</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Seleção: cartão existente ou novo */}
              {existingCards.length > 0 && (
                <div className="space-y-3">
                  <Label>Forma de Pagamento</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={!useNewCard ? "default" : "outline"}
                      onClick={() => setUseNewCard(false)}
                      className="flex-1"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Usar Cartão Cadastrado
                    </Button>
                    <Button
                      type="button"
                      variant={useNewCard ? "default" : "outline"}
                      onClick={() => setUseNewCard(true)}
                      className="flex-1"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Novo Cartão
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista de cartões existentes */}
              {!useNewCard && existingCards.length > 0 && (
                <div className="space-y-3">
                  <Label>Selecione o Cartão</Label>
                  <div className="space-y-2">
                    {existingCards.map((card) => (
                      <Card
                        key={card.id}
                        className={cn(
                          "cursor-pointer transition-all hover:shadow-md",
                          selectedCardId === card.id && "ring-2 ring-primary",
                        )}
                        onClick={() => setSelectedCardId(card.id)}
                      >
                        <CardContent className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">
                                {card.brand} •••• {card.last_four_digits}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {card.holder_name}
                              </p>
                            </div>
                          </div>
                          {selectedCardId === card.id && (
                            <CheckCircle className="h-5 w-5 text-primary" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário de pagamento */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Campos de novo cartão - quando useNewCard ou não tem cartões */}
                {(useNewCard || existingCards.length === 0) && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="holder_name">Nome no Cartão</Label>
                      <Input
                        id="holder_name"
                        value={cardData.holder_name}
                        onChange={(e) =>
                          setCardData({
                            ...cardData,
                            holder_name: e.target.value.toUpperCase(),
                          })
                        }
                        placeholder="NOME COMPLETO COMO NO CARTÃO"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="number">Número do Cartão</Label>
                      <Input
                        ref={cardNumberInputRef}
                        id="number"
                        value={formatCardNumber(cardData.number)}
                        onChange={(e) =>
                          setCardData({
                            ...cardData,
                            number: e.target.value.replace(/\D/g, ""),
                          })
                        }
                        placeholder="0000 0000 0000 0000"
                        maxLength={19}
                        required
                        disabled={loading}
                      />
                      {cardData.number && (
                        <div className="flex items-center gap-2 mt-2">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Bandeira: {getCardBrand(cardData.number)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="exp_month">Mês</Label>
                        <Input
                          id="exp_month"
                          value={cardData.exp_month}
                          onChange={(e) =>
                            setCardData({
                              ...cardData,
                              exp_month: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          placeholder="MM"
                          maxLength={2}
                          required
                          disabled={loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="exp_year">Ano</Label>
                        <Input
                          id="exp_year"
                          value={cardData.exp_year}
                          onChange={(e) =>
                            setCardData({
                              ...cardData,
                              exp_year: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          placeholder="AA"
                          maxLength={2}
                          required
                          disabled={loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cvv">CVV</Label>
                        <Input
                          id="cvv"
                          type="password"
                          value={cardData.cvv}
                          onChange={(e) =>
                            setCardData({
                              ...cardData,
                              cvv: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          placeholder="000"
                          maxLength={4}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Badges de segurança */}
                    <div className="flex flex-wrap items-center justify-center gap-3 py-4 bg-muted/20 rounded-lg">
                      <Badge variant="outline" className="gap-1">
                        <Lock className="h-3 w-3" />
                        Conexão SSL
                      </Badge>
                      <Badge variant="outline" className="gap-1">
                        <Shield className="h-3 w-3" />
                        PCI-DSS
                      </Badge>
                      <Badge variant="outline" className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Criptografia 256-bit
                      </Badge>
                    </div>

                    <div className="text-center text-xs text-muted-foreground bg-muted/20 rounded-lg p-3">
                      <Lock className="h-4 w-4 inline mr-1" />
                      Seus dados de cartão são criptografados e não são
                      armazenados em nossos servidores
                    </div>
                  </>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep("payment-method")}
                    className="flex-1 group"
                    disabled={loading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 group"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    {loading ? "Processando..." : "Confirmar Assinatura"}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {/* PROCESSANDO */}
          {currentStep === "processing" && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-12 space-y-6"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
                <div className="relative bg-primary/10 p-6 rounded-full">
                  <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <p className="text-lg font-medium">
                  {progressMessage || "Processando..."}
                </p>
                <p className="text-sm text-muted-foreground">
                  Não feche esta janela
                </p>
              </div>
            </motion.div>
          )}

          {/* PASSO PIX: QR CODE PARA PAGAMENTO */}
          {currentStep === "pix-success" && pixResult && (
            <motion.div
              key="pix-success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center justify-center py-6 space-y-6"
            >
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">Pague via PIX</h2>
                <p className="text-muted-foreground">
                  Escaneie o QR code ou copie o código abaixo para pagar
                </p>
              </div>
              {pixResult.pix_qr_code_url && (
                <div className="p-4 bg-white rounded-xl border-2 border-primary/20">
                  <img
                    src={pixResult.pix_qr_code_url}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                </div>
              )}
              <div className="w-full space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Código PIX (copia e cola)
                </Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={pixResult.pix_qr_code}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(pixResult!.pix_qr_code);
                      toast.success("Código copiado!");
                    }}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Card className="w-full">
                <CardContent className="pt-6 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plano</span>
                    <span className="font-semibold">{pixResult.plan_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor</span>
                    <span className="font-bold text-primary">
                      {formatPrice(pixResult.amount_cents)}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <p className="text-sm text-muted-foreground text-center">
                Após o pagamento, sua assinatura será ativada automaticamente em
                instantes.
              </p>
              <Button
                onClick={() => handleOpenChange(false)}
                className="w-full"
              >
                Entendi
              </Button>
            </motion.div>
          )}

          {/* PASSO 3: SUCESSO CARTÃO */}
          {currentStep === "success" && subscriptionResult && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center justify-center py-6 space-y-6"
            >
              {/* Ícone de sucesso animado */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" />
                <div className="relative bg-gradient-to-br from-green-500/20 to-primary/20 p-8 rounded-full border-4 border-green-500/30">
                  <CheckCircle
                    className="h-20 w-20 text-green-600"
                    strokeWidth={2.5}
                  />
                </div>
              </motion.div>

              {/* Mensagem principal */}
              <div className="text-center space-y-3">
                <motion.h2
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl font-bold bg-gradient-to-r from-green-600 to-primary bg-clip-text text-transparent"
                >
                  Pagamento Aprovado! 🎉
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-lg text-muted-foreground"
                >
                  Bem-vindo ao{" "}
                  <span className="font-semibold text-primary">
                    {fullPlanData?.name || planName}
                  </span>
                </motion.p>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-sm text-muted-foreground"
                >
                  Pagamento aprovado! Seu plano será ativado em instantes.
                  Atualize a página se necessário.
                </motion.p>
              </div>

              {/* Detalhes da assinatura */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="w-full"
              >
                <Card className="w-full border-2 border-green-500/20 shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-green-500/10 to-primary/10 pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Detalhes da Assinatura
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <Crown className="h-4 w-4" />
                        Plano
                      </span>
                      <span className="font-semibold text-base">
                        {fullPlanData?.name || planName}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Valor mensal
                      </span>
                      <span className="font-bold text-xl text-primary">
                        {formatPrice(fullPlanData?.price_cents || planPrice)}
                        /mês
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Próxima cobrança
                      </span>
                      <span className="text-sm font-medium">
                        {formatDate(subscriptionResult.nextBillingDate)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Status
                      </span>
                      <Badge className="bg-green-600 text-white hover:bg-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Ativa
                      </Badge>
                    </div>
                    {fullPlanData?.max_patients && (
                      <>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Limite de pacientes
                          </span>
                          <span className="text-sm font-medium">
                            {fullPlanData.max_patients === null
                              ? "Ilimitado"
                              : `Até ${fullPlanData.max_patients}`}
                          </span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Próximos passos */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="w-full"
              >
                <Card className="w-full">
                  <CardHeader className="bg-gradient-to-r from-primary/5 to-purple-500/5">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ArrowRight className="h-5 w-5 text-primary" />
                      Próximos passos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.9 }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <BarChart3 className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          Explore suas estatísticas
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Acompanhe seu desempenho e métricas
                        </p>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.0 }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          Comece a adicionar pacientes
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Gerencie seus pacientes de forma eficiente
                        </p>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.1 }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <FileUp className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          Faça upload de documentos
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Organize exames e laudos com IA
                        </p>
                      </div>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Botões */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="flex gap-3 w-full pt-4"
              >
                <Button
                  variant="outline"
                  onClick={() => {
                    onSuccess();
                    onOpenChange(false);
                    // handleOpenChange já navega para /app quando fechar na tela de sucesso
                  }}
                  className="flex-1"
                  size="lg"
                >
                  Fechar
                </Button>
                <Button
                  onClick={() => {
                    onSuccess();
                    onOpenChange(false);
                    navigate("/app");
                  }}
                  className="flex-1 group bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 text-white shadow-lg"
                  size="lg"
                >
                  <Sparkles className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
                  Começar a usar agora
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal de seleção de plano */}
        <PlanSelectorDialog
          open={showPlanSelector}
          onOpenChange={setShowPlanSelector}
          plans={availablePlans}
          currentPlanCode={fullPlanData?.code || planCode}
          onSelectPlan={handleChangePlan}
        />
      </DialogContent>
    </Dialog>
  );
}
